import I18n from '@/modules/i18n';
import {
  plato_api_program_path,
  shared_program_glossaries_path,
  shared_program_lesson_glossary_cards_path,
} from 'modules/routes';
import IGlossaryTerm from '@/components/interfaces/GlossaryTerm';
import Axios from 'axios';
import { compareGlossaryTerms } from '@/utils/sortGlossaryTerms';

const definedTerm = (term: IGlossaryTerm, locale: Locale = 'en') => {
  const termKey = locale === 'en' ? 'term_en' : 'term_es';
  let vocabTerm = term[termKey];

  if (locale === 'es' && vocabTerm.toLowerCase().includes('spanish placeholder')) {
    vocabTerm = term.term_en;
  }

  return vocabTerm;
};

const getDefinition = (term: IGlossaryTerm, locale: Locale = 'en') => {
  if (locale === 'en') return term.definition_en;

  if (locale === 'es' && !term.definition_es.toLowerCase().includes('spanish placeholder')) {
    return term.definition_es;
  }

  return term.definition_en;
};

export const sortedTerms = (terms: IGlossaryTerm[], locale: Locale = 'en') => (
  [...terms].sort((a, b) => compareGlossaryTerms(definedTerm(a, locale), definedTerm(b, locale), locale))
);

const sortedTermsHtml = (terms: IGlossaryTerm[], locale: Locale = 'en') => (
  sortedTerms(terms, locale).map(term => `<p class="vocab-term">${definedTerm(term, locale)}</p>`).join('')
);

export async function fetchEdition(programId: string | number): Promise<string> {
  try {
    const res = await Axios.get(plato_api_program_path(programId));
    const isFourthEdition = res.data.program.edition_en === 'Fourth';
    const isElementary = ['K', '0', '1', '2', '3', '4', '5'].includes(res.data.program.grade.toString());

    return isFourthEdition && isElementary ? 'fourth-edition-elementary' : '';
  } catch (error) {
    console.error(error);

    return '';
  }
}

export const vocabBoxHtml = (
  edition: string,
  lessonId: number | string,
  locale: Locale,
  programId: number | string,
  terms: IGlossaryTerm[],
) => {
  const vocabLink = shared_program_lesson_glossary_cards_path(programId, lessonId);
  const glossaryLink = shared_program_glossaries_path(programId, { lesson_id: lessonId });

  return (`
    <div class="vocab-box ${edition}">
      <div class="vocab-box-header">
        <p class="vocab-title">${I18n.t('vocabulary', { locale })}</p>
        <div>
          <a id="dynamic-vocab-cards-link" class="vocab-btn" href="${vocabLink}" target="_blank">
            ${I18n.t('vocabulary_cards', { locale })}
          </a>
          <a id="dynamic-glossary" class="vocab-btn" href="${glossaryLink}" target="_blank">
            ${I18n.t('glossary', { locale })}
          </a>
        </div>
      </div>
      <div class="vocab-terms">
        ${sortedTermsHtml(terms, locale)}
      </div>
    </div>
  `);
};

const lessonOpenerCardImg = (term: IGlossaryTerm, locale: Locale) => {
  if (!term.s3_url) return '';
  if (term.s3_url.includes('missing')) return '';

  return `<img class="loc-img" alt="${definedTerm(term, locale)}" src="${term.s3_url}" />`;
};

const lessonOpenerCardHtml = (term: IGlossaryTerm, locale: Locale) => {
  const imgHtml = lessonOpenerCardImg(term, locale);
  const hasImg = imgHtml !== '';

  return (`
    <div class="loc ${hasImg ? 'with-img' : ''}">
      <div class="loc-header">
        <span class="heading2 loc-term">${definedTerm(term, locale)}</span>
        ${imgHtml}
      </div>
      <div class="lesson-opener-card-body">
        <span class="body lg loc-definition">${getDefinition(term, locale)}</span>
      </div>
    </div>
  `);
};

const lessonOpenerListItem = (term: IGlossaryTerm, locale: Locale, academic = false) => {
  const termClass = academic ? 'lol-item-term academic' : 'lol-item-term';

  return (`
  <li class="lol-item">
    <span class="${termClass}">${definedTerm(term, locale)}</span> - ${getDefinition(term, locale)}
  </li>
`);
};

const lessonOpenerContent = (
  terms: IGlossaryTerm[],
  locale: Locale,
  type: string = 'list',
  academic = false,
) => {
  if (type === 'card') {
    return sortedTerms(terms, locale).map(term => lessonOpenerCardHtml(term, locale)).join('\n');
  }

  return sortedTerms(terms, locale).map(term => lessonOpenerListItem(term, locale, academic)).join('\n');
};

export const lessonOpenerCardsHtml = (
  terms: IGlossaryTerm[],
  locale: Locale,
) => (`
  <div class="loc-grid" contenteditable="false">
    ${lessonOpenerContent(terms, locale, 'card')}
  </div>
`);

export const lessonOpenerListHtml = (
  terms: IGlossaryTerm[],
  locale: Locale,
  academic = false,
) => (`
  <ul class="loc-list${academic ? ' academic' : ''}" contenteditable="false">
    ${lessonOpenerContent(terms, locale, 'list', academic)}
  </ul>
`);

const replaceContent = (nodes: NodeListOf<Element>, contentFor: (node: Element) => string) => {
  const elements = Array.from(nodes);

  for (let i = 0; i < elements.length; i += 1) {
    elements[i].innerHTML = contentFor(elements[i]);
  }
};

// AIDEV-NOTE: PLAT-17813 — every match is refreshed, not just the first: a section can hold both a
// lesson and an academic list, and the previous singular querySelector only ever updated one.
export const refreshTermsInEditor = (terms: IGlossaryTerm[], {
  academicTerms = [],
  locale = 'en',
  selector = 'body',
}: { academicTerms?: IGlossaryTerm[], locale: Locale, selector: string }) => {
  replaceContent(
    document.querySelectorAll(`${selector} .vocab-terms`),
    () => sortedTermsHtml(terms, locale),
  );

  replaceContent(
    document.querySelectorAll(`${selector} .loc-list`),
    (list) => {
      const academic = list.classList.contains('academic');

      return lessonOpenerContent(academic ? academicTerms : terms, locale, 'list', academic);
    },
  );

  replaceContent(
    document.querySelectorAll(`${selector} .loc-grid`),
    () => lessonOpenerContent(terms, locale, 'card'),
  );
};

export const cleanUpDeleteButton = (vocabElementId: string) => {
  const vocabElementDeleteContainer = document.getElementById(vocabElementId);
  if (vocabElementDeleteContainer) vocabElementDeleteContainer.remove();

  const vocabDeleteButton = document.getElementById('vocabDeleteButton');
  if (vocabDeleteButton) vocabDeleteButton.remove();
};
